import { useLazyQuery } from '@apollo/client';
import { client } from 'apolloClient/client';
import { cloneDeep, omit } from 'lodash';
import { SEARCH_DATA } from 'apolloClient/queries/searchListings';
import { ListingFiltersInput } from 'apolloClient/types/filters';
import { SearchPageResponse } from 'apolloClient/types/Searches';
import { FiltersValues, PropertyType } from 'components/UnitsFilters/types';
import { flattenStrapiBulkDataItems } from 'lib/flattenStrapiBulkDataItems';
import {
  addAndCriteria,
  compareDefaultValues,
} from 'components/Search/utils/listingsFiltersUtils';
import { Building } from '../apolloClient/types';
import { useAuth } from 'components/Auth/AuthProvider';
import { useFavorite } from 'components/SavedHouses/SavedFavoritesContext';
import { useEffect, useMemo, useState } from 'react';
import { updateSearchgFavoritesQuery } from 'src/utils/updateListingFavoritesQuery';
import { isEqualValues } from 'src/utils/utils';
import {
  initialFilters,
  initialFiltersStatuses,
  initialFiltersEmptyListingsType,
  isListingTypesEmpty,
} from '../constants/initialSearchFilter';
import { formatCoordinates } from 'lib/coordinates';
import {
  defaultSortOption,
  defaultFilterValues,
} from 'components/UnitsFilters/constants';
import { transformSearchPageListings } from 'src/utils/searchPageDataTransformer';
import { useRouter } from 'next/router';
import { setQueryParams } from 'components/Search/utils/searchUtils';
import { actualizeSortOptions } from 'componentsV2/UnitsFiltersV2/utilsV2';
import { PARAMS_TO_OMIT_FOR_SERVER } from 'constants/search/allowedSearchParams';

export const getPropTypeQuery = (
  propType: PropertyType
): ListingFiltersInput => {
  if (propType === 'rent') return { rentUnit: { id: { notNull: true } } };
  return { saleUnit: { id: { notNull: true } } };
};

export const createBaseQuery = (
  buildingId?: number,
  propertyType?: PropertyType,
  values?: FiltersValues
) => {
  let filters: ListingFiltersInput = {};
  if (buildingId) {
    filters = Object.assign<ListingFiltersInput, ListingFiltersInput>(filters, {
      and: [
        { unit: { building: { id: { eq: Number(buildingId) } } } },
        {
          or: [
            { saleUnit: { id: { notNull: true } } },
            { rentUnit: { id: { notNull: true } } },
          ],
        },
      ],
    });
  } else {
    filters = Object.assign<ListingFiltersInput, ListingFiltersInput>(
      filters,
      cloneDeep(initialFilters)
    );
    if (isListingTypesEmpty(values)) {
      addAndCriteria(filters, initialFiltersEmptyListingsType);
    } else {
      addAndCriteria(filters, initialFiltersStatuses);
    }
  }
  if (propertyType) {
    filters = Object.assign<ListingFiltersInput, ListingFiltersInput>(
      filters,
      getPropTypeQuery(propertyType)
    );
  }
  return filters;
};

const updateSearchCache = (
  prevQueryResult: SearchPageResponse,
  options: { fetchMoreResult: SearchPageResponse }
): SearchPageResponse => {
  return {
    ...prevQueryResult,
    ...options.fetchMoreResult,
  };
};

export const useSearch = ({
  buildingId,
  pageSize,
  searchValues,
}: {
  buildingId?: number;
  pageSize?: number;
  searchValues?: { [index: string]: any };
}) => {
  const router = useRouter();
  const { getActualAuthData, me } = useAuth();

  const { latestAction } = useFavorite();

  const [buildingsData, setBuildingsData] = useState<Building[]>(
    [] as Building[]
  );
  const [isBuildignsLoaded, setIsBuildignsLoaded] = useState(false);

  const [isPaginating, setIsPaginating] = useState(false);

  const [
    refetchSearchData,
    {
      data: searchData,
      loading: searchLoading,
      variables,
      updateQuery,
      fetchMore,
    },
  ] = useLazyQuery<
    SearchPageResponse,
    {
      filters: { [index: string]: any };
      pageSize: number;
      page: number;
      loadBuildings: boolean;
      buildingId: number;
    }
  >(SEARCH_DATA, {
    fetchPolicy: 'network-only', // TODO: For some reason no-cache policy here breaking saving favorites on the search page. We need to investigate why they works only with network-only
    client,
    variables: {
      filters: searchValues
        ? omit(searchValues, [...PARAMS_TO_OMIT_FOR_SERVER])
        : {},
      pageSize: pageSize || 20,
      page: 0,
      loadBuildings: true,
      buildingId: buildingId || -1,
    },
  });

  const [prevFilters, setPrevFilters] = useState<{ [index: string]: any }>({});

  useEffect(() => {
    if (latestAction) {
      updateQuery(
        updateSearchgFavoritesQuery(latestAction, 'getSearchPageData')
      );
    }
  }, [latestAction]);

  const refetchQueryResults = ({
    values,
    loadBuildings,
    sortOption,
    propertyType,
    page,
    isFilterClearing,
    collectionListingIds,
  }: {
    values: { [index: string]: any };
    loadBuildings?: boolean;
    sortOption?: string;
    propertyType?: PropertyType;
    page?: number;
    isFilterClearing?: boolean;
    // Shared collection: show exactly these listings, ignore URL filters.
    collectionListingIds?: string[] | null;
  }) => {
    const isCollection = !!collectionListingIds?.length;
    const comparisonValues = isCollection
      ? { listingIds: collectionListingIds }
      : values;
    const isFiltersChanged = !isEqualValues(prevFilters, comparisonValues);
    const isPrevSearchBoxFiltersInit = prevFilters?.searchBoxFilters?.buildings;
    const isSearchBoxFiltersChanged =
      isPrevSearchBoxFiltersInit &&
      !isEqualValues(prevFilters?.searchBoxFilters, values.searchBoxFilters);
    const { user } = getActualAuthData();

    const filters = compareDefaultValues(values, defaultFilterValues);
    // used to handle the V2 naming of sort options
    const actualSortOption = actualizeSortOptions(sortOption);
    // TODO: Refactoring.
    // Taking coordinates from url if filters empty
    const sw = (router.query?.sw as string)?.split?.(',');
    const ne = (router.query?.ne as string)?.split?.(',');
    const zoomFromUrl = parseInt(router.query?.z as string, 10);
    let coordinates;

    if (
      sw &&
      ne &&
      !filters?.coordinates &&
      !isSearchBoxFiltersChanged &&
      !isFilterClearing
    ) {
      coordinates = formatCoordinates(sw, ne);
    }

    const hasPolygons = !!filters?.polygons?.length;
    const hasCoordinates = !!(coordinates || filters?.coordinates);

    const parameters = {
      filters: isCollection
        ? {
            listingIds: collectionListingIds,
            sortOption: actualSortOption || defaultSortOption,
          }
        : {
            ...omit(filters, [...PARAMS_TO_OMIT_FOR_SERVER]),
            coordinates:
              isSearchBoxFiltersChanged || hasPolygons
                ? undefined
                : hasCoordinates
                ? coordinates || filters?.coordinates
                : undefined,
            polygons: hasPolygons ? filters.polygons : undefined,
            sortOption: actualSortOption || defaultSortOption,
          },
      pageSize: pageSize || 20,
      page: page || 0,
      authorized: !!user.id || false,
      userId: user.id || -1,
      buildingId: buildingId || -1,
      loadBuildings: loadBuildings || false,
    };
    if (isFiltersChanged) {
      setPrevFilters(comparisonValues);

      if (loadBuildings) {
        setIsBuildignsLoaded(false);
      }

      refetchSearchData({
        variables: {
          ...parameters,
        },
      });

      if (loadBuildings && searchData?.getSearchPageData?.buildings) {
        setBuildingsData([]);
      }
    } else {
      fetchMore({
        variables: {
          ...parameters,
          loadBuildings: false,
        },
        updateQuery: updateSearchCache,
      });
    }

    // setQueryParams self-guards against no-op updates (compares old vs new
    // query strings before navigating), so always call it when on /search.
    // A collection URL (?collection=<code>) must not be rewritten from filters.
    if (router.pathname === '/search' && !isCollection) {
      // TODO: refactoring (isSearchBoxFiltersChanged...). Create function getUpdatedQueryParams...
      setQueryParams(
        {
          ...(filters as FiltersValues),
          coordinates:
            isSearchBoxFiltersChanged || isFilterClearing
              ? undefined
              : filters?.coordinates || coordinates || undefined,
          z:
            isSearchBoxFiltersChanged || isFilterClearing
              ? undefined
              : filters?.z || zoomFromUrl || undefined,
          sw:
            isSearchBoxFiltersChanged || isFilterClearing
              ? undefined
              : filters?.sw,
          ne:
            isSearchBoxFiltersChanged || isFilterClearing
              ? undefined
              : filters.ne,
          page: page ? '' + page : '0',
          sortOption: actualSortOption || defaultSortOption,
        },
        defaultFilterValues,
        router
      );
    }
  };

  const transformedListings = useMemo(() => {
    const listingsData = searchData?.getSearchPageData?.listings?.data;
    if (listingsData) {
      return listingsData.map((listing) =>
        transformSearchPageListings(listing)
      );
    }
  }, [searchData?.getSearchPageData?.listings]);
  const listings = flattenStrapiBulkDataItems(transformedListings);
  const pagination = searchData?.getSearchPageData?.listings?.meta?.pagination;

  const paginate = (page: number, sortOption?: string) => {
    const actualSortOption = actualizeSortOptions(sortOption);
    if (variables) {
      setIsPaginating(true);
      fetchMore({
        variables: {
          ...variables,
          filters: {
            ...variables?.filters,
            sortOption: actualSortOption || defaultSortOption,
          },
          buildingId: buildingId || -1, // Use the current buildingId, not the one from variables
          page,
          loadBuildings: false,
        },
        updateQuery: updateSearchCache,
      }).finally(() => {
        setIsPaginating(false); // Reset when done
      });
    }
  };

  useEffect(() => {
    if (!buildingsData.length && searchData) {
      setBuildingsData(searchData?.getSearchPageData?.buildings);
      setIsBuildignsLoaded(true);
    }
  }, [searchData]);

  // eslint-disable-next-line max-len
  const buildings = useMemo(
    () => (buildingsData?.length ? buildingsData : []),
    [buildingsData]
  );

  return {
    listings,
    refetchQueryResults,
    pagination,
    variables,
    paginate,
    buildings,
    buildingsLoading: searchLoading,
    listingsLoading: searchLoading,
    isBuildignsLoaded,
    isPaginating,
  };
};
